import { useState } from 'react';
import {
  Building2,
  Trash2,
  Check,
  X,
  ExternalLink,
  Loader2,
  Inbox,
  RefreshCw,
  Briefcase,
  Star,
  ScanText,
} from 'lucide-react';
import type { SavedJob } from '@/lib/types';
import type { AssessmentSummaryItem, Dimension } from '@/lib/assessment';
import { scoreColor, DIMENSION_META } from '@/lib/assessment';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Props = {
  jobs: SavedJob[];
  summaries?: Record<string, AssessmentSummaryItem>;
  loading: boolean;
  error: string | null;
  onSelect: (job: SavedJob) => void;
  onDelete: (id: string) => void;
  onToggleStar: (job: SavedJob) => void;
  onRefresh: () => void;
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function JobList({
  jobs,
  summaries,
  loading,
  error,
  onSelect,
  onDelete,
  onToggleStar,
  onRefresh,
}: Props) {
  // Which job's removal is awaiting a second click. Using an inline two-step
  // confirm instead of window.confirm avoids modal dialogs and accidental
  // deletes.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading your saved jobs…
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
        <Button variant="outline" onClick={onRefresh} className="w-full">
          <RefreshCw className="h-4 w-4" /> Try again
        </Button>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
        <div className="rounded-full bg-muted p-4">
          <Inbox className="h-8 w-8" />
        </div>
        <div>
          <p className="text-base font-medium text-foreground">No saved jobs yet</p>
          <p className="mt-1 text-sm">
            Browse a supported HK job site (JobsDB, CTgoodjobs, Indeed, Recruit,
            Moovup) in Chrome and use the JobFit side panel to save listings.
          </p>
        </div>
        <Button variant="outline" onClick={onRefresh} className="mt-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {jobs.map((job) => (
        <li
          key={job.id}
          className="rounded-lg border border-border bg-card shadow-sm transition-colors hover:bg-accent/40"
        >
          <div className="flex items-start gap-2 px-4 pt-3">
            <button
              type="button"
              onClick={() => {
                setConfirmingId(null);
                onSelect(job);
              }}
              className="flex min-w-0 flex-1 items-start gap-3 text-left"
            >
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Briefcase className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
                  {job.title}
                </p>
                {job.advertiser ? (
                  <p className="mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground">
                    <Building2 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{job.advertiser}</span>
                  </p>
                ) : null}
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  {job.source ? (
                    <span className="rounded bg-secondary px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-secondary-foreground">
                      {job.source}
                    </span>
                  ) : null}
                  {timeAgo(job.createdAt)}
                </p>
                {(() => {
                  const summary = summaries?.[job.id];
                  const bdGenerating = summary?.breakdownGenerating;
                  const bdReady = summary?.breakdownStatus === 'ready';
                  if (bdGenerating) {
                    return (
                      <p className="mt-1.5 flex items-center gap-1 text-[11px] text-primary">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                        Breaking down JD…
                      </p>
                    );
                  }
                  if (summary?.generating) {
                    return (
                      <p className="mt-1.5 flex items-center gap-1 text-[11px] text-primary">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                        Generating quiz…
                      </p>
                    );
                  }
                  const latest = summary?.latest;
                  return (
                    <>
                      {/* Breakdown-done badge — a small ScanText icon that tells
                          the user this job has been analyzed. Sits inline so it
                          doesn't take vertical space on cards without it. */}
                      {bdReady && !latest ? (
                        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                          <ScanText className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                          <span className="text-emerald-600">JD analyzed</span>
                        </p>
                      ) : null}
                      {latest ? (
                        <p className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
                          {(latest.dimensions ?? ([
                            'willingness',
                            'hard_requirements',
                            'soft_requirements',
                          ] as Dimension[])).map((dim) => {
                            const score =
                              dim === 'willingness'
                                ? latest.willingnessScore.score
                                : dim === 'hard_requirements'
                                  ? latest.hardScore.score
                                  : latest.softScore.score;
                            const Icon = DIMENSION_META[dim].icon;
                            return (
                              <span key={dim} className="inline-flex items-center gap-1">
                                <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
                                <span className={cn('font-semibold tabular-nums', scoreColor(score))}>
                                  {score}
                                </span>
                              </span>
                            );
                          })}
                          <span className="text-muted-foreground/70">· assessed {timeAgo(latest.createdAt)}</span>
                        </p>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleStar(job);
              }}
              aria-label={job.starred ? 'Unstar' : 'Star'}
              aria-pressed={job.starred}
              className={cn(
                'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
                job.starred
                  ? 'text-amber-500 hover:bg-amber-500/10'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <Star
                className="h-4 w-4"
                fill={job.starred ? 'currentColor' : 'none'}
              />
            </button>
          </div>
          <div className="flex items-center justify-end gap-1 border-t border-border/60 px-2 py-1.5">
            <a
              href={job.url}
              target="_blank"
              rel="noreferrer"
              className={cn(
                'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium',
                'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open
            </a>
            {confirmingId === job.id ? (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmingId(null);
                  }}
                  title="Cancel"
                  aria-label="Cancel remove"
                  className={cn(
                    'inline-flex h-9 w-9 items-center justify-center rounded-md',
                    'bg-destructive/10 text-destructive hover:bg-destructive/20',
                  )}
                >
                  <X className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmingId(null);
                    onDelete(job.id);
                  }}
                  title="Confirm remove"
                  aria-label="Confirm remove saved job"
                  autoFocus
                  className={cn(
                    'inline-flex h-9 w-9 items-center justify-center rounded-md',
                    'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20',
                  )}
                >
                  <Check className="h-4 w-4" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingId(job.id);
                }}
                className={cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium',
                  'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
                )}
                aria-label="Remove saved job"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}